import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import cx from 'classnames';
import { tableFrameClass } from '@app/constants/styles';
import { useIsMobile } from '@ui/hooks/use-mobile';
import { formatEur } from './budget';
import type { IncidentCostLine, IncidentCostQuarter } from './incidentCosts';
import { isFolded, readFolds, rememberFolds, setFold } from './chapterFolds';
import {
  costColumns,
  gridHeadTint,
  hoursCell,
  hoursFoldBox,
  hoursFoldOpen,
  hoursFoldShut,
  hoursHeadCell,
  hoursNameCell,
  hoursNameFlush,
  hoursRow,
} from './sprintGrid';
import SprintScrollRail from './SprintScrollRail';

const dash = '—';
const columns = { gridTemplateColumns: costColumns };
// Swapped rather than overridden: two justify utilities are settled by emit order, not by class order.
const leftCell = hoursCell.replace('justify-end', 'justify-start');
const leftHead = hoursHeadCell.replace('justify-end', 'justify-start');

const times = (rate: number): string => `×${Number(rate.toFixed(2))}`;
const hoursText = (hours: number): string => `${Number(hours.toFixed(2))} h`;

/** Incident work is charged per quarter, so it is read here per quarter rather than per sprint. */
const IncidentCostsCard = ({
  title,
  hint,
  emptyText,
  quarters,
  isError,
}: {
  title: string;
  hint: string;
  emptyText: string;
  quarters: IncidentCostQuarter[];
  isError: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const scroller = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const unpriced = quarters.some(quarter => quarter.lines.some(line => line.cost === null));
  const { productId = '' } = useParams<{ productId: string }>();
  // Read before the first paint, as the other cards' folds are, so an arrival does not replay a collapse.
  const [folded, setFolded] = useState(() => readFolds(productId, 'incidents'));
  const fold = (key: string, shut: boolean): void =>
    setFolded(current => {
      const next = setFold(current, key, shut);
      rememberFolds(productId, next, 'incidents');
      return next;
    });

  const multiplier = (line: IncidentCostLine): string =>
    [
      times(line.priorityRate),
      ...line.bands.map(({ band, rate }) => `${t(`spend.incidents.bands.${band}`)} ${times(rate)}`),
    ].join(' · ');

  return (
    <section className="rounded-2xl border border-border bg-card shadow-sm">
      <div className="px-5 pb-3 pt-4">
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">{hint}</p>
      </div>

      {isError ? (
        <p className="px-5 pb-5 text-sm text-muted-foreground">{t('cardReadFailed')}</p>
      ) : (
        <div ref={scroller} className={cx(tableFrameClass, 'no-scrollbar mb-5')}>
          {/* The names scroll with the figures on a phone, as on the out of pocket card. */}
          <div role="table" aria-label={title} className="min-w-max max-md:[&_.sticky]:static">
            <div role="row" style={columns} className={hoursRow}>
              <div role="columnheader" className={cx(hoursNameCell, 'font-medium')}>
                {t('spend.incidents.incident')}
              </div>
              <div role="columnheader" className={cx(leftHead, gridHeadTint)}>
                {t('spend.incidents.priority')}
              </div>
              <div role="columnheader" className={cx(hoursHeadCell, gridHeadTint)}>
                {t('spend.incidents.hours')}
              </div>
              <div role="columnheader" className={cx(hoursHeadCell, gridHeadTint)}>
                {t('spend.incidents.base')}
              </div>
              <div role="columnheader" className={cx(hoursHeadCell, gridHeadTint)}>
                {t('spend.incidents.multiplier')}
              </div>
              <div role="columnheader" className={cx(hoursHeadCell, gridHeadTint)}>
                {t('spend.incidents.cost')}
              </div>
            </div>

            {!quarters.length && (
              <div role="row" className="px-3 py-3 text-sm text-muted-foreground">
                {emptyText}
              </div>
            )}

            {quarters.map(quarter => {
              const shut = isFolded(folded, quarter.quarter);
              return (
                <div key={quarter.quarter} role="rowgroup">
                  <div role="row" style={columns} className={hoursRow}>
                    <div role="rowheader" className={cx(hoursNameFlush, 'font-semibold')}>
                      <button
                        type="button"
                        onClick={() => fold(quarter.quarter, !shut)}
                        aria-expanded={!shut}
                        className="flex size-full cursor-pointer items-center gap-1.5 px-3 text-left font-semibold text-foreground"
                      >
                        <ChevronDown
                          className={cx(
                            'size-4 shrink-0 text-muted-foreground transition-transform duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none',
                            shut && '-rotate-90',
                          )}
                        />
                        <span className="truncate">{t(`budget.quarters.${quarter.quarter}`)}</span>
                        {/* Faded rather than added, as on the workforce grid, so the count eases in. */}
                        <span
                          data-fold-count
                          aria-hidden={!shut}
                          className={cx(
                            'shrink-0 rounded-full bg-muted px-1.5 text-xs font-normal tabular-nums text-muted-foreground',
                            'transition-opacity duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none',
                            shut ? 'opacity-100' : 'opacity-0',
                          )}
                        >
                          {quarter.lines.length}
                        </span>
                      </button>
                    </div>
                    <div role="cell" className={cx(hoursCell, gridHeadTint)} />
                    <div role="cell" className={cx(hoursCell, gridHeadTint, 'font-semibold tabular-nums')}>
                      {hoursText(quarter.hours)}
                    </div>
                    <div role="cell" className={cx(hoursCell, gridHeadTint)} />
                    <div role="cell" className={cx(hoursCell, gridHeadTint)} />
                    <div role="cell" className={cx(hoursCell, gridHeadTint, 'font-semibold tabular-nums')}>
                      {quarter.cost === null ? dash : formatEur(quarter.cost)}
                    </div>
                  </div>
                  {/* Clipped, so folding cuts the incidents off the bottom and leaves their rows their own height. */}
                  <div
                    role="rowgroup"
                    style={{ '--rows': quarter.lines.length } as React.CSSProperties}
                    className={cx(hoursFoldBox, shut ? hoursFoldShut : hoursFoldOpen)}
                  >
                    {quarter.lines.map(line => (
                      <div key={line.key} role="row" style={columns} className={hoursRow}>
                        <div role="rowheader" className={hoursNameFlush}>
                          <span className="truncate pl-7 pr-3" title={line.title}>
                            {line.title || t('spend.incidents.untitled')}
                          </span>
                        </div>
                        <div role="cell" className={leftCell}>
                          {line.priority ? line.priority.toUpperCase() : dash}
                        </div>
                        <div role="cell" className={cx(hoursCell, 'tabular-nums')}>
                          {hoursText(line.hours)}
                        </div>
                        <div role="cell" className={cx(hoursCell, 'tabular-nums')}>
                          {line.base === null ? dash : formatEur(line.base)}
                        </div>
                        <div role="cell" className={cx(hoursCell, 'tabular-nums')}>
                          <span className="truncate" title={multiplier(line)}>
                            {multiplier(line)}
                          </span>
                        </div>
                        <div role="cell" className={cx(hoursCell, 'tabular-nums')}>
                          {line.cost === null ? dash : formatEur(line.cost)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <SprintScrollRail
            scroller={scroller}
            nameWidth={isMobile ? undefined : 'var(--cost-name-width)'}
            columns={4}
          />
        </div>
      )}
      {unpriced && <p className="-mt-2 px-5 pb-4 text-sm text-muted-foreground">{t('spend.incidents.unpriced')}</p>}
    </section>
  );
};

export default IncidentCostsCard;
